import "@fontsource-variable/archivo";
import "@fontsource-variable/recursive";
import "@web-git-graph/web/register";
import { GitHubGitGraphProvider } from "@web-git-graph/web/providers/github";
import { HttpGitGraphProvider } from "@web-git-graph/web/providers/http";
import type { GitGraphPage } from "@web-git-graph/protocol";
import { GitGraphProtocolError } from "@web-git-graph/protocol";
import type { WebGitGraphElement } from "@web-git-graph/web";
import historyFixture from "./history-fixture.json";
import { initPage } from "./page";
import "./style.css";

type StatusState =
  | { kind: "fixture" }
  | { kind: "connecting"; repository: string }
  | { kind: "connected"; backend: string; repository: string }
  | { kind: "tip" }
  | { kind: "rateLimited" }
  | { kind: "error"; message: string };

const TOKEN_KEY = "web-git-graph.demo.github-token";

const messages = {
  en: {
    "nav.demo": "Demo",
    "nav.how": "How it works",
    "nav.guide": "Get started",
    "nav.protocol": "HTTP protocol",
    "theme.toDark": "Switch to dark theme",
    "theme.toLight": "Switch to light theme",
    "hero.eyebrow": "Web Component · Zero framework dependencies",
    "hero.titleA": "Git history,",
    "hero.titleB": "on any page.",
    "hero.lede": "One embeddable element that renders commit graphs from GitHub, your own backend, or a local repository — with the same UI everywhere.",
    "hero.try": "Try the live demo",
    "hero.install": "Get started",
    "hero.caption": "the actual component UI",
    "facts.framework": "framework dependencies",
    "facts.element": "custom element",
    "facts.providers": "built-in providers",
    "facts.endpoints": "read-only protocol endpoints",
    "demo.kicker": "Live demo",
    "demo.title": "Try it live.",
    "demo.description": "Search, switch refs, open commit details inline, or Ctrl/Cmd-click two commits to compare. Load any public GitHub repository.",
    "demo.history": "Repository history",
    "demo.repoLabel": "GitHub repository",
    "demo.load": "Load",
    "demo.tokenLabel": "GitHub token (optional)",
    "demo.tokenPlaceholder": "Paste a token if GitHub rate-limits you — stored only in this tab",
    "demo.statusFixture": "Showing sample history from react/react (offline snapshot). Load a public repository for live data.",
    "demo.fullscreen": "Fullscreen",
    "share.title": "Share this graph",
    "share.body": "The fullscreen page reads the repository from its URL, so a link opens straight into that graph. Drop the badge into a README and readers reach it in one click.",
    "share.url": "Fullscreen link",
    "share.markdown": "README badge (Markdown)",
    "share.html": "HTML anchor",
    "share.preview": "Badge preview",
    "how.kicker": "How it works",
    "how.title": "Swap the data. Keep the UI.",
    "how.description": "The component talks to one small provider interface. Change where the commits come from — the layout and interactions stay identical.",
    "how.stage1.tag": "Data source",
    "how.stage1.title": "A provider fetches history",
    "how.stage1.body": "GitHub REST, an HTTP v1 backend, or local Git — all normalized to the same typed DTOs.",
    "how.stage2.tag": "Layout",
    "how.stage2.title": "Lanes are computed deterministically",
    "how.stage2.body": "The same commits always produce the same graph, and lanes stay stable across pages.",
    "how.stage3.tag": "Render",
    "how.stage3.title": "The element draws the graph",
    "how.stage3.body": "Virtualized rows in an accessible treegrid, themeable with CSS custom properties.",
    "how.noteA.title": "Read-only by design",
    "how.noteA.body": "No checkout, merge, rebase, or reset is exposed. Git commands never pass through a shell.",
    "how.noteB.title": "Any framework",
    "how.noteB.body": "The same element works in plain HTML, React, Vue, Svelte, and Angular.",
    "how.noteC.title": "One shared contract",
    "how.noteC.body": "DTOs, JSON Schemas, and the OpenAPI document live in @web-git-graph/protocol.",
    "guide.kicker": "Get started",
    "guide.title": "Install, register, connect.",
    "guide.description": "Two lines of setup, then pick where the commits come from.",
    "guide.step1": "Install",
    "guide.step2": "Register the element",
    "guide.step3": "Connect a data source",
    "connect.github.tab": "GitHub direct",
    "connect.github.body": "Read public repositories straight from the browser — no server required.",
    "connect.http.tab": "HTTP backend",
    "connect.http.body": "Serve history from your own infrastructure — any backend that implements the six read-only endpoints of the HTTP v1 protocol.",
    "connect.http.link": "Read the HTTP v1 protocol reference →",
    "connect.local.tab": "Local repository",
    "connect.local.body": "One command serves a local repository over the same protocol. It binds to 127.0.0.1, and filesystem paths never reach the browser.",
    "callout.title": "The protocol behind every backend",
    "callout.body": "Six read-only JSON endpoints, a versioned media type, and a typed error model — documented with examples.",
    "callout.action": "Protocol reference →",
    "closing.title": "History is easier to trust when you can see it.",
    "closing.demo": "Open the demo",
    "closing.github": "View on GitHub ↗",
    "footer.tagline": "Framework-free Git history for the web.",
    "common.copy": "Copy",
    "common.copied": "Copied",
    "status.fixture": "Showing sample history from react/react (offline snapshot). Load a public repository for live data.",
    "status.connecting": "Connecting to github.com/{repository}…",
    "status.connected": "Connected to {backend} · repository {repository}",
    "status.tip": "Tip: Ctrl/Cmd-click another commit to compare. Double-click to open it on GitHub.",
    "status.error": "GitHub API: {message}",
    "status.rateLimited": "Rate limit reached (unauthenticated GitHub API allows 60 requests/hour per IP). Paste a personal access token above to continue — classic token with public_repo is enough, and it stays in this tab only."
  },
  zh: {
    "nav.demo": "在线演示",
    "nav.how": "工作原理",
    "nav.guide": "快速开始",
    "nav.protocol": "HTTP 协议",
    "theme.toDark": "切换到深色主题",
    "theme.toLight": "切换到浅色主题",
    "hero.eyebrow": "原生 Web Component · 零框架依赖",
    "hero.titleA": "让 Git 历史",
    "hero.titleB": "出现在任何页面。",
    "hero.lede": "一个可嵌入的原生组件,渲染来自 GitHub、自有后端或本地仓库的提交历史——界面与交互始终一致。",
    "hero.try": "体验在线演示",
    "hero.install": "快速开始",
    "hero.caption": "组件的真实界面",
    "facts.framework": "框架依赖",
    "facts.element": "个自定义元素",
    "facts.providers": "种内置数据源",
    "facts.endpoints": "个只读协议端点",
    "demo.kicker": "在线演示",
    "demo.title": "直接试用。",
    "demo.description": "搜索、切换分支、原位展开提交详情,或按住 Ctrl/Cmd 选择两个提交进行比较。也可以载入任意公开 GitHub 仓库。",
    "demo.history": "仓库历史",
    "demo.repoLabel": "GitHub 仓库",
    "demo.load": "载入",
    "demo.tokenLabel": "GitHub token（可选）",
    "demo.tokenPlaceholder": "若触发 GitHub 限流请粘贴 token——仅保存在本标签页",
    "demo.statusFixture": "当前展示 react/react 的离线示例历史。输入公开仓库可载入实时数据。",
    "demo.fullscreen": "全屏",
    "share.title": "分享这张图",
    "share.body": "全屏页面从 URL 读取仓库，因此一个链接就能直接打开对应的图。把徽章放进 README，读者点一下即可到达。",
    "share.url": "全屏链接",
    "share.markdown": "README 徽章（Markdown）",
    "share.html": "HTML 链接标签",
    "share.preview": "徽章预览",
    "how.kicker": "工作原理",
    "how.title": "换掉数据源,界面不变。",
    "how.description": "组件只依赖一个很小的 Provider 接口。无论提交来自哪里,布局和交互都保持一致。",
    "how.stage1.tag": "数据源",
    "how.stage1.title": "Provider 拉取历史",
    "how.stage1.body": "GitHub REST、HTTP v1 后端或本地 Git,统一成同一套类型化 DTO。",
    "how.stage2.tag": "布局",
    "how.stage2.title": "泳道按确定性算法计算",
    "how.stage2.body": "相同的提交永远得到相同的图,分页加载时泳道保持稳定。",
    "how.stage3.tag": "渲染",
    "how.stage3.title": "组件绘制图谱",
    "how.stage3.body": "虚拟滚动的可访问 treegrid,可用 CSS 自定义属性适配主题。",
    "how.noteA.title": "只读设计",
    "how.noteA.body": "不暴露 checkout、merge、rebase、reset;Git 命令永不经过 shell。",
    "how.noteB.title": "任意框架",
    "how.noteB.body": "同一个元素可用于原生 HTML、React、Vue、Svelte 与 Angular。",
    "how.noteC.title": "一份共享契约",
    "how.noteC.body": "DTO、JSON Schema 与 OpenAPI 文档都在 @web-git-graph/protocol 中。",
    "guide.kicker": "快速开始",
    "guide.title": "安装、注册、连接。",
    "guide.description": "两行代码完成初始化,然后选择提交数据的来源。",
    "guide.step1": "安装",
    "guide.step2": "注册组件",
    "guide.step3": "连接数据源",
    "connect.github.tab": "GitHub 直连",
    "connect.github.body": "浏览器直接读取公开仓库,无需任何服务端。",
    "connect.http.tab": "HTTP 后端",
    "connect.http.body": "用自己的基础设施提供历史数据——任何实现 HTTP v1 协议六个只读端点的后端都可以。",
    "connect.http.link": "查看 HTTP v1 协议参考 →",
    "connect.local.tab": "本地仓库",
    "connect.local.body": "一条命令即可用同一协议提供本地仓库。默认只监听 127.0.0.1,文件路径永远不会到达浏览器。",
    "callout.title": "每个后端背后的协议",
    "callout.body": "六个只读 JSON 端点、带版本的媒体类型与类型化错误模型,附完整示例。",
    "callout.action": "协议参考 →",
    "closing.title": "看得见的历史,更值得信任。",
    "closing.demo": "打开在线演示",
    "closing.github": "在 GitHub 查看 ↗",
    "footer.tagline": "为 Web 而生的零框架 Git 历史图。",
    "common.copy": "Copy",
    "common.copied": "Copied",
    "status.fixture": "当前展示 react/react 的离线示例历史。输入公开仓库可载入实时数据。",
    "status.connecting": "正在连接 github.com/{repository}…",
    "status.connected": "已连接 {backend} · 仓库 {repository}",
    "status.tip": "提示:按住 Ctrl/Cmd 选择另一个提交进行比较;双击可在 GitHub 打开。",
    "status.error": "GitHub API:{message}",
    "status.rateLimited": "已触发速率限制（未认证 GitHub API 每个 IP 每小时仅 60 次）。请在上方粘贴 personal access token 后重试——classic 勾选 public_repo 即可，token 仅保存在本标签页。"
  }
};

const graph = document.querySelector<WebGitGraphElement>("#graph")!;
const form = document.querySelector<HTMLFormElement>("#repo-form")!;
const repositoryInput = document.querySelector<HTMLInputElement>("#repository")!;
const tokenInput = document.querySelector<HTMLInputElement>("#github-token")!;
const status = document.querySelector<HTMLElement>("#status")!;
const statusLine = document.querySelector<HTMLElement>(".status-line")!;
const fullscreenLink = document.querySelector<HTMLAnchorElement>("#fullscreen-link")!;

let statusState: StatusState = { kind: "fixture" };

const page = initPage({
  messages,
  documentTitle: {
    en: "Web Git Graph — Git history for the web",
    zh: "Web Git Graph — 为 Web 而生的 Git 历史图"
  },
  onLocale: renderStatus,
  onTheme: (theme) => {
    graph.theme = theme;
  }
});

const fixture = historyFixture as GitGraphPage;
graph.data = fixture;
graph.theme = page.theme;
tokenInput.value = sessionStorage.getItem(TOKEN_KEY) ?? "";
renderStatus();
renderShare();
repositoryInput.addEventListener("input", renderShare);

const query = new URLSearchParams(window.location.search);
const backendUrl = query.get("backend");
if (backendUrl) {
  const repositoryId = query.get("repository") ?? "local";
  graph.provider = new HttpGitGraphProvider({
    baseUrl: backendUrl,
    repositoryId
  });
  statusState = { kind: "connected", backend: backendUrl, repository: repositoryId };
  renderStatus();
}

form.addEventListener("submit", (event) => {
  event.preventDefault();
  const normalized = normalizeRepository(repositoryInput.value);
  if (!normalized) return;
  const token = tokenInput.value.trim();
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);

  statusState = { kind: "connecting", repository: normalized };
  renderStatus();
  graph.provider = new GitHubGitGraphProvider({
    repository: normalized,
    pageSize: 80,
    ...(token ? { token } : {})
  });
  graph.addEventListener(
    "gitgraph-error",
    (error) => {
      const raw = error.detail.error;
      const message = raw instanceof Error ? raw.message : String(raw);
      statusState =
        (raw instanceof GitGraphProtocolError && raw.code === "rate_limited") ||
        /rate limit/i.test(message)
          ? { kind: "rateLimited" }
          : { kind: "error", message };
      renderStatus();
      if (statusState.kind === "rateLimited") {
        tokenInput.focus();
        tokenInput.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    },
    { once: true }
  );
  window.setTimeout(() => {
    if (statusState.kind === "connecting") {
      statusState = { kind: "tip" };
      renderStatus();
    }
  }, 900);
});

tokenInput.addEventListener("change", () => {
  const token = tokenInput.value.trim();
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
});

const tabs = document.querySelectorAll<HTMLButtonElement>("[data-tab]");
const panels = document.querySelectorAll<HTMLElement>("[data-tab-panel]");
tabs.forEach((tab) => {
  tab.addEventListener("click", () => {
    tabs.forEach((button) => button.setAttribute("aria-selected", String(button === tab)));
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== tab.dataset.tab;
    });
  });
});

/** `owner/name`, tolerating clone URLs and a trailing `.git`. */
function normalizeRepository(value: string): string {
  return value
    .trim()
    .replace(/^git@github\.com:/i, "")
    .replace(/^(?:https?:\/\/)?(?:www\.)?github\.com\//i, "")
    .replace(/[?#].*$/, "")
    .replace(/\.git$/i, "")
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .slice(0, 2)
    .join("/");
}

/**
 * Keeps the fullscreen link and the README snippets in step with whatever
 * repository is currently in the box, so the reader never has to assemble the
 * query string by hand.
 */
function renderShare(): void {
  const repository = normalizeRepository(repositoryInput.value);
  const stage = new URL("./graph.html", window.location.href).href;
  // Slashes stay literal: `?repo=owner/name` is what people paste into a README.
  const link = repository.includes("/")
    ? `${stage}?repo=${encodeURIComponent(repository).replace(/%2F/g, "/")}`
    : stage;
  const badge = new URL("./brand/git-graph-badge.svg", window.location.href).href;
  fullscreenLink.href = link;
  const snippets = {
    "share-url": link,
    "share-markdown": `[![git graph](${badge})](${link})`,
    "share-html": `<a href="${link}"><img src="${badge}" alt="git graph" height="20" /></a>`
  };
  for (const [id, snippet] of Object.entries(snippets)) {
    const field = document.querySelector<HTMLElement>(`#${id}`)!;
    field.textContent = snippet;
    // initPage's copy handler reads data-copy at click time, so rewriting the
    // attribute is enough to keep the buttons current.
    field.parentElement
      ?.querySelector<HTMLButtonElement>("[data-copy]")
      ?.setAttribute("data-copy", snippet);
  }
}

function renderStatus(): void {
  statusLine.dataset.state = statusState.kind;
  if (statusState.kind === "fixture") {
    status.textContent = page.t("status.fixture");
  } else if (statusState.kind === "connecting") {
    status.textContent = page.t("status.connecting", { repository: statusState.repository });
  } else if (statusState.kind === "connected") {
    status.textContent = page.t("status.connected", {
      backend: statusState.backend,
      repository: statusState.repository
    });
  } else if (statusState.kind === "tip") {
    status.textContent = page.t("status.tip");
  } else if (statusState.kind === "rateLimited") {
    status.textContent = page.t("status.rateLimited");
  } else {
    status.textContent = page.t("status.error", { message: statusState.message });
  }
}
